import { UtmParameters } from '@shared/core/router.helper/RoutesContext';

/**
 * Partial UTM parameters type for flexible usage.
 * Allows partial UTM parameters (not all three required).
 */
export type PartialUtmParameters = Partial<UtmParameters & { readonly utm_source_secondary?: string }>;

/**
 * Merges UTM parameters with priority: existing parameters override internal parameters.
 *
 * This function merges two sets of UTM parameters where existing parameters (typically from URL)
 * take precedence over internal parameters (typically defaults). This ensures that campaign/ads
 * tracking parameters are preserved and not overridden by internal defaults.
 *
 * @param existingUtm - UTM parameters from existing URL (highest priority - overrides internal)
 * @param internalUtm - Internal UTM parameters to use as fallback (lower priority)
 * @returns Merged UTM parameters object with existing values taking precedence
 */
export const mergeUtmParameters = (existingUtm?: PartialUtmParameters, internalUtm?: PartialUtmParameters): PartialUtmParameters => {
  // Spread internal first (base), then existing (overrides) - existing takes precedence
  return { ...internalUtm, ...existingUtm };
};

/**
 * Builds a UTM query string from the provided UTM parameters.
 * https://withjoy.atlassian.net/wiki/x/CgAw3g
 *
 * @param utm_medium - The utm_medium through which the traffic is coming (e.g. "web", "email", "text")
 * @param utm_source - The utm_source of the traffic (e.g. "website_accommodations_page", "confirmation_rsvp", "confirmation_registry_purchase")
 * @param utm_campaign - The utm_campaign of the marketing campaign (e.g. "explore_other_options", "post_rsvp_hotel_grid", "next_steps")
 * @returns {string} The formatted UTM query string (e.g. "utm_medium=web&utm_source=website_accommodations_page&utm_campaign=explore_other_options")
 */

/**
 * Applies UTM parameters to a URL string.
 * Merges UTM parameters into the target URL, preserving existing query parameters.
 *
 * @param url - The target URL to apply UTM parameters to
 * @param utm - Partial UTM parameters to apply
 * @returns The URL with UTM parameters merged in
 */
export const applyUtmParametersToUrl = (url: string, utm: PartialUtmParameters): string => {
  try {
    const urlObj = new URL(url);
    if (utm.utm_medium) {
      urlObj.searchParams.set('utm_medium', utm.utm_medium);
    }
    if (utm.utm_source) {
      urlObj.searchParams.set('utm_source', utm.utm_source);
    }
    if (utm.utm_campaign) {
      urlObj.searchParams.set('utm_campaign', utm.utm_campaign);
    }
    return urlObj.toString();
  } catch {
    // If URL parsing fails, try simple string concatenation for relative URLs
    const separator = url.includes('?') ? '&' : '?';
    const params: string[] = [];
    if (utm.utm_medium) {
      params.push(`utm_medium=${encodeURIComponent(utm.utm_medium)}`);
    }
    if (utm.utm_source) {
      params.push(`utm_source=${encodeURIComponent(utm.utm_source)}`);
    }
    if (utm.utm_campaign) {
      params.push(`utm_campaign=${encodeURIComponent(utm.utm_campaign)}`);
    }
    return params.length > 0 ? `${url}${separator}${params.join('&')}` : url;
  }
};
